import { clsx } from 'clsx';

import type { ReeferBaseProps } from '../../utils/baseProps.types';
import { Image } from '../image/image';
import { useReeferTheme } from '../providers/reeferThemeProvider/reeferThemeProvider';
import { Typography } from '../typography/typography';
import defaultStoreImage from './assets/default-store-logo.svg';

import styles from './partnerLogo.module.css';

type PartnerLogoSizeType = 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export const PARTNER_LOGO_SIZES = {
  xxs: 32,
  xs: 48,
  sm: 64,
  md: 72,
  lg: 96,
  xl: 120,
};

const getBorderRadius = (size: PartnerLogoSizeType) =>
  size === 'md' ? 'lg' : size === 'xxs' ? 'xs' : size;

interface LogoProps extends ReeferBaseProps {
  /** Path or url to logo image */
  image?: string | null;

  /** Set to false to skip auto-generated srcSet and sizes */
  isResponsive?: boolean;

  /** Store or Brand name, used for label and generating placeholder initials for the brand variant */
  name: string;

  /** Size of the icon: xxs (32px), xs (48px), sm (64px), lg (96px), xl (120px) */
  size?: PartnerLogoSizeType;
}

function BrandLogo({
  className,
  'data-testid': testId,
  id,
  image,
  isResponsive = true,
  name,
  size = 'lg',
  style,
}: LogoProps) {
  const label = image ? `Logo for ${name}` : `Brand initials for ${name}`;
  const brandInitials = name
    .split(' ')
    .map((word) => word[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return !image ? (
    <div
      aria-label={label}
      className={clsx(
        className,
        styles['partnerLogo'],
        styles[`partnerLogo__${size}`]
      )}
      data-testid={testId}
      id={id}
      style={style}
      role="img"
    >
      {!image && (
        <Typography
          variant={
            size === 'xxs'
              ? 'body-bold'
              : ['xs', 'sm'].includes(size)
                ? 'header-bold'
                : 'title-bold'
          }
          as="span"
          color="grays-black"
        >
          {brandInitials}
        </Typography>
      )}
    </div>
  ) : (
    <Image
      altText={label}
      className={className}
      id={id}
      src={image}
      height={`${PARTNER_LOGO_SIZES[size]}px`}
      width={`${PARTNER_LOGO_SIZES[size]}px`}
      borderRadius="circular"
      border
      objectPosition="50% 50%"
      objectFit="contain"
      data-testid={testId}
      sizes={isResponsive ? 'thumbnail' : undefined}
      style={style}
      responsive={isResponsive}
    />
  );
}

function StoreLogo({
  className,
  'data-testid': testId,
  id,
  image,
  isResponsive = true,
  name,
  size = 'lg',
  style,
}: LogoProps) {
  const theme = useReeferTheme();
  const label = image
    ? `Logo for ${name}`
    : 'Placeholder image of storefront with cannabis leaf';

  const imageSrc = image || defaultStoreImage;

  return (
    <Image
      altText={label}
      className={className}
      id={id}
      src={imageSrc}
      height={`${PARTNER_LOGO_SIZES[size]}px`}
      width={`${PARTNER_LOGO_SIZES[size]}px`}
      borderRadius={theme.borderRadius[getBorderRadius(size)] as string}
      border
      objectPosition="50% 50%"
      objectFit="contain"
      data-testid={testId}
      sizes={image && isResponsive ? 'thumbnail' : undefined}
      style={style}
      responsive={!!image && isResponsive}
    />
  );
}

export interface PartnerLogoProps extends LogoProps {
  /** Determines component styling. */
  variant: 'store' | 'brand';
}

/**
 * The `PartnerLogo` component is used to display a logo for our store and brand partners.
 */
export function PartnerLogo({
  className,
  'data-testid': testId,
  id,
  name,
  image,
  isResponsive,
  size = 'lg',
  style,
  variant = 'brand',
}: PartnerLogoProps) {
  if (variant === 'store') {
    return (
      <StoreLogo
        className={className}
        id={id}
        name={name}
        image={image}
        isResponsive={isResponsive}
        data-testid={testId}
        style={style}
        size={size}
      />
    );
  } else {
    return (
      <BrandLogo
        className={className}
        id={id}
        name={name}
        image={image}
        isResponsive={isResponsive}
        data-testid={testId}
        style={style}
        size={size}
      />
    );
  }
}
